<script setup lang="ts">
import { ref, computed } from 'vue'
import type { ApocryphaBook } from '../../types/apocrypha'
import CascadeSelect from '../bible-reading/CascadeSelect.vue'

interface Props {
  selectedBook?: string
  isLoading?: boolean
  apocryphaBooks?: ApocryphaBook[]
}

const props = withDefaults(defineProps<Props>(), {
  selectedBook: 'all',
  isLoading: false,
  apocryphaBooks: () => [],
})

const emit = defineEmits<{
  'book-change': [bookId: string]
  'search': [query: string, bookId: string]
}>()

const searchQuery = ref('')
const isBookSelectOpen = ref(false)

const bookOptions = computed(() => {
  const options = [{ label: '全部次經', value: 'all' }]
  props.apocryphaBooks.forEach((book) => {
    options.push({
      label: `${book.name_zh}`,
      value: book.id.toString(),
    })
  })
  return options
})

const handleBookChange = (value: string): void => {
  emit('book-change', value)
  isBookSelectOpen.value = false
}

const handleBookSelectOpenChange = (value: boolean): void => {
  isBookSelectOpen.value = value
}

const handleSearch = (): void => {
  if (searchQuery.value.trim()) {
    emit('search', searchQuery.value.trim(), props.selectedBook)
  }
}

const handleKeyDown = (e: KeyboardEvent): void => {
  if (e.key === 'Enter') {
    handleSearch()
  } else if (e.key === 'Escape') {
    bookMenuOpen.value = false
  }
}
</script>

<template>
  <div class="apocrypha-toolbar">
    <div class="toolbar-row">
      <div style="width: 150px; flex-shrink: 0">
        <CascadeSelect
          :model-value="selectedBook"
          :options="bookOptions"
          :is-open="isBookSelectOpen"
          :disabled="false"
          placeholder="選擇次經..."
          @update:model-value="handleBookChange"
          @update:is-open="handleBookSelectOpenChange"
        />
      </div>

      <div class="search-input-container">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="輸入關鍵字..."
          :disabled="isLoading"
          @keydown="handleKeyDown"
        />
        <button
          class="search-btn"
          :disabled="isLoading || !searchQuery.trim()"
          @click="handleSearch"
        >
          {{ isLoading ? '搜尋中...' : '搜尋' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.apocrypha-toolbar {
  padding: 8px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 32px;
}

.search-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 200px;
}

.search-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  height: 24px;
  background-color: white;
  color: #1f2937;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.search-input:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.search-btn {
  padding: 4px 12px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  height: 24px;
  transition: background-color 0.2s;
}

.search-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.search-btn:disabled {
  background-color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
